/* Shared helpers for the "Actuals Vs Forecast by Date Range" tab — dynamic YYYY-MM columns
   instead of the fixed jan-dec + ytd columns cm_actuals_vs_forecast uses. */

import { toReportApiFilters } from "../filterRelationshipUtils";

export const MAX_RANGE_MONTHS = 24;
export const BACKEND_MAX_RANGE_MONTHS = 36;

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export const ROW_DEFS = [
  { key: "contractRevenue", label: "Revenue", type: "subtotal", isPct: false },
  { key: "directLabor", label: "Direct Labor", type: "data", isPct: false },
  { key: "subcontractor", label: "Subcontractor", type: "data", isPct: false },
  { key: "odcs", label: "ODCs", type: "data", isPct: false },
  { key: "totalDirectCost", label: "Total Direct Cost", type: "subtotal", isPct: false },
  { key: "grossMargin", label: "Gross Margin", type: "metric", isPct: false },
  { key: "grossMarginPct", label: "Gross Margin %", type: "pct", isPct: true },
  { key: "indirectCost", label: "Indirect Cost", type: "data", isPct: false },
  { key: "totalCost", label: "Total Cost", type: "data", isPct: false },
  { key: "netProfit", label: "Net Profit / Fee", type: "data", isPct: false },
  { key: "netMarginPct", label: "Net Margin %", type: "pct", isPct: true },
];

/** Number of calendar months spanned by a start→end range, inclusive. */
export function monthsInRange(startMonth, startYear, endMonth, endYear) {
  return (endYear - startYear) * 12 + (endMonth - startMonth) + 1;
}

export function isRangeValid(startMonth, startYear, endMonth, endYear) {
  if (!startMonth || !startYear || !endMonth || !endYear) return false;
  const count = monthsInRange(startMonth, startYear, endMonth, endYear);
  return count >= 1 && count <= MAX_RANGE_MONTHS;
}

/** Column defs from the API's own echoed `columns` array ("YYYY-MM"... , "total"). */
export function colsFromKeys(columnKeys) {
  return (columnKeys || []).map((key) => {
    if (key === "total") return { key, label: "Total", isTotal: true };
    const [y, m] = key.split("-").map(Number);
    return { key, label: `${MONTH_LABELS[m - 1]} ${y}`, isTotal: false };
  });
}

export function formatRangeLabel(startMonth, startYear, endMonth, endYear) {
  return `${MONTH_LABELS[startMonth - 1]} ${startYear} – ${MONTH_LABELS[endMonth - 1]} ${endYear}`;
}

/** Matches the backend's download filename convention (raw request params, not the
    possibly-36-month-narrowed response range — see DATE_RANGEAPI.md's Excel Download section). */
export function formatDownloadFilename(startMonth, startYear, endMonth, endYear) {
  return `Actuals_vs_Forecast_${MONTH_LABELS[startMonth - 1]}_${startYear}_to_${MONTH_LABELS[endMonth - 1]}_${endYear}.xlsx`;
}

/** Trailing 12-month window ending the current month — sensible non-empty default. */
export function defaultDateRange() {
  const now = new Date();
  const endMonth = now.getMonth() + 1;
  const endYear = now.getFullYear();
  let startMonth = endMonth - 11;
  let startYear = endYear;
  if (startMonth < 1) { startMonth += 12; startYear -= 1; }
  return { startMonth, startYear, endMonth, endYear };
}

export function buildApiFilters(filters) {
  return toReportApiFilters(filters, [
    "orgId", "contractType", "contractId", "clinLvl2", "businessType", "awardType",
    "competitionType", "bu", "popEndDate", "sector", "projectId",
    "contractName", "customerName",
  ]);
}

// API returns pct rows as ratios (0.238 = 23.8%); multiply by 100 for display.
export function normalizePct(dataset, colKeys) {
  if (!dataset) return dataset;
  const out = { ...dataset };
  ROW_DEFS.forEach(({ key, isPct }) => {
    if (isPct && out[key]) {
      const transformed = {};
      colKeys.forEach((ck) => { transformed[ck] = (out[key][ck] ?? 0) * 100; });
      out[key] = transformed;
    }
  });
  return out;
}

export function computeVariance(live, comparison, colKeys) {
  const out = {};
  ROW_DEFS.forEach(({ key }) => {
    out[key] = {};
    colKeys.forEach((ck) => { out[key][ck] = (live[key]?.[ck] ?? 0) - (comparison[key]?.[ck] ?? 0); });
  });
  return out;
}

/** "Actuals through Mon YYYY" derived from the last entry of an actualMonths ("YYYY-MM") array. */
export function formatActualsThroughFromMonthKeys(actualMonths) {
  if (!actualMonths?.length) return null;
  const last = [...actualMonths].sort().pop();
  const [y, m] = String(last).split("-").map(Number);
  if (!y || !m || m < 1 || m > 12) return null;
  return `Actuals through ${MONTH_LABELS[m - 1]} ${y}`;
}
